import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from './cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'critical' | 'warning';
export type ButtonSize = 'sm' | 'md';

const VARIANTS: Record<ButtonVariant, string> = {
  // Verde de marca. Es el momento de identidad más fuerte del dashboard y por
  // eso el verde vive acá y no en los enlaces: un relleno sólido y un badge
  // pálido de "verificado" no se confunden, un enlace verde sí lo haría.
  primary:
    'bg-brand text-brand-contrast hover:bg-brand-hover disabled:bg-border disabled:text-ink-subtle',
  secondary:
    'border border-border-strong bg-surface text-ink hover:bg-surface-sunken disabled:text-ink-subtle',
  ghost: 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
  // Rojo sólo en lo que destruye o alerta: retirar un lote, revocar una key.
  critical: 'bg-critical text-critical-contrast hover:opacity-90',
  // Ámbar para lo reversible que igual asusta: dar de baja una etiqueta se
  // deshace, marcarla como falsificada no. Que no compartan color es la
  // diferencia que hay que ver antes de apretar.
  //
  // La tinta va OSCURA y no blanca: sobre el ámbar del manual el blanco da
  // 2,04:1 (ver docs/DESIGN-TOKENS.md). Es el mismo motivo por el que el
  // glifo dentro de un círculo de estado es Charcoal.
  warning: 'bg-suspect-fill text-on-status-light hover:opacity-90',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children?: ReactNode;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  className,
  type = 'button',
  ...props
}: ButtonProps): ReactNode {
  return (
    <button
      // `type="button"` por defecto: sin esto, un botón dentro de un
      // formulario lo envía, y en este dashboard "enviar" puede ser un recall.
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-70',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    />
  );
}
